import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Empty, Spin, Typography } from 'antd';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { countryFlag, countryName } from '@/utils/cricketCalendar';
import { LANGUAGE_LABEL, MOMENT_LABEL } from '@/utils/matchPushCopy';
import type { MatchPushPlan, MatchPushPreviewItem } from '@/types/matchPushes';
import { PhoneNotification } from './PhoneNotification';

const { Text, Title } = Typography;

interface Props {
  plan: MatchPushPlan;
  /** Changes when the plan was edited, to reload the preview. */
  version?: string;
}

const groupLabel = (country: string, outcome: string | null): string =>
  `${countryFlag(country)} ${countryName(country)}${outcome ? ` · if ${outcome}` : ''}`;

/** The rendered title and body for every language and version, as phone mock-ups. */
export const PreviewPanel: React.FC<Props> = ({ plan, version }) => {
  const [items, setItems] = useState<MatchPushPreviewItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    adminApi.matchPushes
      .previewPlan(plan.id)
      .then((response) => {
        if (!cancelled) setItems(response.data.items);
      })
      .catch((e) => {
        if (!cancelled) setError(apiErrorMessage(e, 'Failed to load the preview'));
      });
    return () => {
      cancelled = true;
    };
  }, [plan.id, version]);

  const groups = useMemo(() => {
    const map = new Map<string, MatchPushPreviewItem[]>();
    for (const item of items ?? []) {
      const key = `${item.country}|${item.outcome ?? ''}`;
      map.set(key, [...(map.get(key) ?? []), item]);
    }
    return [...map.entries()];
  }, [items]);

  return (
    <Card
      size="small"
      title={
        <Title level={5} style={{ margin: 0 }}>
          Preview: {MOMENT_LABEL[plan.moment]}
        </Title>
      }
    >
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 10 }}>
        Each player gets their own language. The text fills in from the match.
      </Text>
      {error && <Alert type="error" showIcon message={error} />}
      {!items && !error && <Spin />}
      {items && items.length === 0 && <Empty description="Nothing to preview" />}
      {groups.map(([key, group]) => (
        <div key={key}>
          {groups.length > 1 && (
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              {groupLabel(group[0].country, group[0].outcome)}
            </Text>
          )}
          {group.map((item) => (
            <PhoneNotification
              key={`${item.templateId}-${item.language}-${item.version}`}
              caption={`${LANGUAGE_LABEL[item.language]} · version ${item.version}`}
              title={item.title}
              body={item.body}
              note={item.overridden ? 'Custom copy for this match' : undefined}
            />
          ))}
        </div>
      ))}
    </Card>
  );
};
